/**
 * The ADR 060 `Money` value type: an exact Decimal paired with its currency.
 * Arithmetic refuses a currency mismatch, and no constructor takes a JS number.
 */
import Decimal from 'decimal.js';

import { Rate } from '../rate/rate';
import { largestRemainder } from './allocate';
import { isSupportedCurrency, minorUnits, type SupportedCurrency } from './currency';
import { format } from './format';
import { HALF_UP, type RoundingMode } from './rounding';

/**
 * Thrown when two amounts in different currencies meet in one operation. A
 * mismatch is a bug upstream (ADR 060 § 2.3) — don't catch it to "be safe".
 */
export class CurrencyMismatchError extends Error {
    /**
     * @param op    - The operation that refused (`plus`, `compare`, …).
     * @param left  - The receiver's currency.
     * @param right - The argument's currency.
     */
    constructor(op: string, left: SupportedCurrency, right: SupportedCurrency) {
        super(`Money.${op}: currency mismatch (${left} vs ${right})`);
        this.name = 'CurrencyMismatchError';
    }
}

/**
 * A monetary amount: an exact `Decimal` plus the currency it is denominated in.
 * Immutable — every operation returns a new value. `a + b` on two `Money`s is a
 * compile error; use {@link Money.plus}.
 */
export class Money {
    private constructor(
        private readonly amount: Decimal,
        /** ISO 4217 code this amount is denominated in. */
        readonly currency: SupportedCurrency,
    ) {}

    /**
     * Pairs an exact decimal with its currency. Accepts a `Decimal` (what the
     * `moneyNumeric` column type decodes to) or an exact decimal string (what
     * the wire carries) — never a JS `number`, which would already be a float.
     *
     * @param value    - The amount, as a `Decimal` or exact decimal string.
     * @param currency - A supported ISO 4217 code.
     * @returns The `Money`.
     * @throws TypeError on a `number`, a non-finite value, or an unsupported currency.
     */
    static of(value: Decimal | string, currency: SupportedCurrency): Money {
        if (typeof value !== 'string' && !Decimal.isDecimal(value)) {
            // Runtime twin of the type: a `number` smuggled through `as` is a float already.
            throw new TypeError(`Money.of: amount must be a Decimal or decimal string, got ${typeof value}`);
        }
        if (!isSupportedCurrency(currency)) {
            throw new TypeError(`Money.of: "${String(currency)}" is not a supported currency`);
        }
        const amount = new Decimal(value);
        if (!amount.isFinite()) {
            throw new TypeError(`Money.of: amount must be finite, got ${amount.toString()}`);
        }
        return new Money(amount, currency);
    }

    /** @returns The exact amount, for a repository write or a codec. */
    toDecimal(): Decimal {
        return this.amount;
    }

    /**
     * @param other - An amount in the same currency.
     * @returns The exact sum.
     * @throws CurrencyMismatchError when the currencies differ.
     */
    plus(other: Money): Money {
        this.assertSameCurrency('plus', other);
        return new Money(this.amount.plus(other.amount), this.currency);
    }

    /**
     * @param other - An amount in the same currency.
     * @returns The exact difference.
     * @throws CurrencyMismatchError when the currencies differ.
     */
    minus(other: Money): Money {
        this.assertSameCurrency('minus', other);
        return new Money(this.amount.minus(other.amount), this.currency);
    }

    /**
     * Scales by a dimensionless factor and rounds the product to the currency's
     * minor unit. The factor is a `Rate` (tax, premium, commission — applied as
     * its fraction) or a `Decimal` quantity such as acreage. There is no
     * `number` overload: a percentage enters only as a `Rate` (ADR 060 § 2.5).
     *
     * @param factor - A `Rate`, or an exact `Decimal` multiplier.
     * @param mode   - Rounding mode; half-up unless the call site states otherwise.
     * @returns The rounded product, in this amount's currency.
     */
    times(factor: Rate | Decimal, mode: RoundingMode = HALF_UP): Money {
        const multiplier = factor instanceof Rate ? factor.toFraction() : factor;
        return new Money(this.amount.times(multiplier), this.currency).round(mode);
    }

    /**
     * Splits this amount across weights so the parts sum to it exactly, by
     * largest remainder over the currency's minor unit (ADR 060 § 2.4). $100.00
     * across 1:1:1 is 33.34, 33.33, 33.33 — the leftover cent goes to the part
     * owed the most, ties to the earliest. A negative amount splits by magnitude.
     *
     * @param weights - Non-negative finite weights with a positive sum, one per part.
     * @returns One `Money` per weight, in this currency, summing to this amount.
     * @throws RangeError on an unusable weight list, or when this amount carries
     *   residue below its minor unit (whose half-cent would it be?) — round first.
     */
    allocate(weights: readonly (Decimal | number)[]): Money[] {
        const exponent = minorUnits(this.currency);
        const units = this.amount.abs().times(new Decimal(10).pow(exponent));
        if (!units.isInteger()) {
            throw new RangeError(
                `Money.allocate: ${this.amount.toString()} ${this.currency} is below its minor unit; round it first`,
            );
        }
        const parts = largestRemainder(units, weights.map((w) => new Decimal(w)));
        const sign = this.amount.isNegative() ? -1 : 1;
        return parts.map((p) => new Money(p.dividedBy(new Decimal(10).pow(exponent)).times(sign), this.currency));
    }

    /**
     * @param mode - Rounding mode; half-up unless the call site states otherwise.
     * @returns This amount rounded to its currency's minor unit (JPY → 0 dp, USD → 2).
     */
    round(mode: RoundingMode = HALF_UP): Money {
        return new Money(this.amount.toDecimalPlaces(minorUnits(this.currency), mode), this.currency);
    }

    /**
     * @param other - An amount in the same currency.
     * @returns -1, 0 or 1.
     * @throws CurrencyMismatchError when the currencies differ.
     */
    compare(other: Money): -1 | 0 | 1 {
        this.assertSameCurrency('compare', other);
        return this.amount.comparedTo(other.amount) as -1 | 0 | 1;
    }

    /**
     * @param other - An amount in the same currency.
     * @returns True when the values are equal (`2.00` equals `2.0000`).
     * @throws CurrencyMismatchError when the currencies differ.
     */
    equals(other: Money): boolean {
        this.assertSameCurrency('equals', other);
        return this.amount.equals(other.amount);
    }

    /** @returns True when the amount is exactly zero. */
    isZero(): boolean {
        return this.amount.isZero();
    }

    /** @returns True when the amount is below zero. */
    isNegative(): boolean {
        return this.amount.isNegative() && !this.amount.isZero();
    }

    /** @returns The absolute value. */
    abs(): Money {
        return new Money(this.amount.abs(), this.currency);
    }

    /** @returns The amount with its sign flipped. */
    negate(): Money {
        return new Money(this.amount.negated(), this.currency);
    }

    /** @returns The display string — so an accidental `${money}` still renders an amount. */
    toString(): string {
        return format(this);
    }

    private assertSameCurrency(op: string, other: Money): void {
        if (other.currency !== this.currency) {
            throw new CurrencyMismatchError(op, this.currency, other.currency);
        }
    }
}
